import { RefObject, useEffect, useState } from 'react'

type Status = 'processing' | 'ready' | 'failed'

/** Pixels whose darkest channel is above this are treated as white background. */
const BG_THRESHOLD = 232
/** Pixels between this and BG_THRESHOLD next to the background get a soft edge. */
const FEATHER_FLOOR = 190

/**
 * Removes a flat white background from an image and paints the result
 * straight into the given canvas (no PNG re-encoding, so it is near-instant).
 * Only white connected to the image edges is removed, so white details inside
 * the product are preserved. Falls back to 'failed' if the canvas can't read it.
 *
 * `displayWidth` is the largest CSS width the image is shown at; the canvas is
 * sized for that × device pixel ratio, so small screens process far fewer pixels.
 */
export function useTransparentImage(
  src: string,
  canvasRef: RefObject<HTMLCanvasElement>,
  displayWidth: number,
) {
  const [status, setStatus] = useState<Status>('processing')

  useEffect(() => {
    let cancelled = false
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.decoding = 'async'

    img.onload = () => {
      if (cancelled) return
      const canvas = canvasRef.current
      if (!canvas) return
      try {
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        const target = Math.round(displayWidth * dpr)
        const scale = Math.min(
          1,
          target / Math.max(img.naturalWidth, img.naturalHeight),
        )
        const w = Math.round(img.naturalWidth * scale)
        const h = Math.round(img.naturalHeight * scale)
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx) throw new Error('no-context')
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, w, h)
        const imageData = ctx.getImageData(0, 0, w, h)
        removeBackground(imageData.data, w, h)
        ctx.putImageData(imageData, 0, 0)
        setStatus('ready')
      } catch {
        setStatus('failed')
      }
    }
    img.onerror = () => {
      if (!cancelled) setStatus('failed')
    }
    img.src = src

    return () => {
      cancelled = true
    }
  }, [src, canvasRef, displayWidth])

  return status
}

function removeBackground(data: Uint8ClampedArray, w: number, h: number) {
  const total = w * h
  const minChannel = (i: number) => {
    const o = i * 4
    const r = data[o]
    const g = data[o + 1]
    const b = data[o + 2]
    return r < g ? (r < b ? r : b) : g < b ? g : b
  }
  const removed = new Uint8Array(total)
  const stack = new Int32Array(total)
  let top = 0

  const seed = (i: number) => {
    if (!removed[i] && minChannel(i) >= BG_THRESHOLD) {
      removed[i] = 1
      stack[top++] = i
    }
  }
  for (let x = 0; x < w; x++) {
    seed(x)
    seed((h - 1) * w + x)
  }
  for (let y = 0; y < h; y++) {
    seed(y * w)
    seed(y * w + w - 1)
  }

  while (top > 0) {
    const i = stack[--top]
    const x = i % w
    if (x > 0) seed(i - 1)
    if (x < w - 1) seed(i + 1)
    if (i >= w) seed(i - w)
    if (i < total - w) seed(i + w)
  }

  for (let i = 0; i < total; i++) {
    const o = i * 4
    if (removed[i]) {
      data[o + 3] = 0
      continue
    }
    const x = i % w
    const touchesBg =
      (x > 0 && removed[i - 1]) ||
      (x < w - 1 && removed[i + 1]) ||
      (i >= w && removed[i - w]) ||
      (i < total - w && removed[i + w])
    if (!touchesBg) continue
    const m = minChannel(i)
    if (m > FEATHER_FLOOR) {
      const alpha = (BG_THRESHOLD - m) / (BG_THRESHOLD - FEATHER_FLOOR)
      data[o + 3] = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    }
  }
}
